import { useState, useMemo } from 'react';
import { useUsers } from '../../lib/supabase/users/supabaseUserService';
import { useHolidays } from '../../lib/supabase/holidays/supabaseHolidayService';
import { LoadingState } from '../ui/loading-state';
import { getUserName } from '../../utils/format';
import { DateTime } from 'luxon';
import { ExpandableDataTable } from '../ExpandableDataTable';
import { ColumnDef } from '@tanstack/react-table';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Badge } from "../ui/badge";
import { Breadcrumbs } from '../ui/breadcrumbs';

type TimeOffRow = {
  user_id: string;
  userName: string;
  totalDaysOff: number;
  daysInMonth: number;
  percentageOff: number;
  departments: string[];
  holidayTypes: {
    vacation: number;
    sick: number;
    personal: number;
  };
  holidays: Array<{
    startDate: DateTime;
    endDate: DateTime;
    type: 'vacation' | 'sick' | 'personal';
  }>;
};

const STORAGE_KEY_PREFIX = 'shiftly-timeoff-';

const getHolidayColor = (type: 'vacation' | 'sick' | 'personal' | null) => {
  switch (type) {
    case 'vacation':
      return 'bg-brand-gold dark:bg-brand-gold';
    case 'sick':
      return 'bg-brand-red dark:bg-brand-red';
    case 'personal':
      return 'bg-brand-green dark:bg-brand-green';
    default:
      return 'bg-brand-dark dark:bg-brand-dark';
  }
};

const TimeOffView = () => {
  const [departmentFilter, setDepartmentFilter] = useState<string>(() => {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}department`) || 'all';
  });

  const { data: users = [], isLoading: usersLoading } = useUsers();

  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const dateRange = useMemo(() => {
    const now = DateTime.utc();

    // Start of current month at 6am
    const startDate = DateTime.fromObject({
      year: now.year,
      month: now.month,
      day: 1,
      hour: 6,
      minute: 0,
      second: 0,
      millisecond: 0
    }, { zone: 'UTC' });

    // End of current month at 6am
    const endDate = startDate.endOf('month').set({ hour: 6 });

    return {
      startDate: startDate,
      endDate: endDate
    };
  }, []);

  const { data: holidays = [], isLoading: holidaysLoading } = useHolidays(
    dateRange.startDate,
    dateRange.endDate
  );

  const processedData = useMemo(() => {
    if (usersLoading || holidaysLoading || !users.length) return [];

    const daysInMonth = dateRange.startDate.daysInMonth || 0;
    const userHolidays = new Map<string, TimeOffRow>();

    holidays.forEach(holiday => {
      const user = users.find(u => u.id === holiday.user_id);
      if (!user) return;

      const userName = getUserName(users, holiday.user_id);

      if (!userHolidays.has(holiday.user_id)) {
        userHolidays.set(holiday.user_id, {
          user_id: holiday.user_id,
          userName,
          totalDaysOff: 0,
          daysInMonth,
          percentageOff: 0,
          departments: holiday.departments || [],
          holidayTypes: {
            vacation: 0,
            sick: 0,
            personal: 0
          },
          holidays: []
        });
      }

      const userEntry = userHolidays.get(holiday.user_id)!;

      // Calculate days off for this holiday using 6am-6am boundaries
      const holidayStart = holiday.start_date
        .set({ hour: 6, minute: 0, second: 0, millisecond: 0 })
        .minus({ days: 1 }); // Move start back one day to show in correct square
      const holidayEnd = holiday.end_date
        .set({ hour: 6, minute: 0, second: 0, millisecond: 0 })
        .minus({ days: 1 }); // Move end back one day to show in correct square

      // Calculate days between the adjusted boundaries
      const daysDiff = Math.floor(holidayEnd.diff(holidayStart, 'days').days);

      userEntry.totalDaysOff += daysDiff;
      userEntry.holidayTypes[holiday.type] += daysDiff;
      userEntry.percentageOff = (userEntry.totalDaysOff / daysInMonth) * 100;
      userEntry.holidays.push({
        startDate: holiday.start_date,
        endDate: holiday.end_date,
        type: holiday.type
      });
    });

    // Filter by department if needed
    return Array.from(userHolidays.values())
      .filter(row => {
        if (departmentFilter === 'all') return true;
        return row.departments.includes(departmentFilter as 'Sales' | 'Mass DMs' | 'Posts' | 'GG Swap');
      });
  }, [users, holidays, departmentFilter, dateRange.startDate, usersLoading, holidaysLoading]);

  const columns = useMemo<ColumnDef<TimeOffRow>[]>(() => [
    {
      accessorKey: 'userName',
      header: 'User',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.getValue('userName')}</span>
          {row.original.departments.map((dept) => (
            <Badge key={dept} variant="outline" className="text-xs">
              {dept}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'timeOffBreakdown',
      header: 'Time Off Types',
      enableSorting: false,
      cell: ({ row }) => {
        const types = row.original.holidayTypes;
        return (
          <div className="space-y-1">
            {types.vacation > 0 && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-brand-gold text-brand-dark dark:bg-brand-dark/30 dark:text-brand-gold">
                  Vacation: {types.vacation} days
                </Badge>
              </div>
            )}
            {types.sick > 0 && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-brand-red text-brand-red dark:bg-brand-dark/30 dark:text-brand-red">
                  Sick: {types.sick} days
                </Badge>
              </div>
            )}
            {types.personal > 0 && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-brand-green text-brand-dark dark:bg-brand-dark/30 dark:text-brand-green">
                  Personal: {types.personal} days
                </Badge>
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'timeOffProgress',
      header: 'Days Off This Month',
      accessorFn: (row) => row.totalDaysOff,
      cell: ({ row }) => {
        const totalDays = row.original.totalDaysOff;
        const daysInMonth = row.original.daysInMonth;
        const percentage = row.original.percentageOff;

        return (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">{totalDays} / {daysInMonth} days</span>
              <span className="text-muted-foreground">{percentage.toFixed(1)}%</span>
            </div>
            <div className="w-full h-2 bg-brand-dark dark:bg-brand-dark rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-gold dark:bg-brand-gold"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      },
    }
  ], []);

  const renderExpanded = (row: TimeOffRow) => {
    const monthStart = dateRange.startDate.startOf('day');
    const monthEnd = dateRange.endDate.startOf('day');
    const dayCount = Math.floor(monthEnd.diff(monthStart, 'days').days) + 1;

    const days = Array.from({ length: dayCount }, (_, i) => {
      const currentDay = monthStart.plus({ days: i });
      const dayEnd = currentDay.set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
      const dayStart = dayEnd.minus({ days: 1 });
      return { date: currentDay, start: dayStart, end: dayEnd };
    });

    // Get holiday type for each day, accounting for 6am-6am boundaries
    const getHolidayTypeForDay = (dayBoundary: { start: DateTime; end: DateTime }) => {
      for (const holiday of row.holidays) {
        // A holiday starting at 6am on day X should first appear in day X+1's square
        // A holiday ending at 6am on day Y should last appear in day Y-1's square
        const holidayStart = holiday.startDate
          .set({ hour: 6, minute: 0, second: 0, millisecond: 0 })
          .minus({ days: 1 }); // Move start back one day to show in correct square
        const holidayEnd = holiday.endDate
          .set({ hour: 6, minute: 0, second: 0, millisecond: 0 })
          .minus({ days: 1 }); // Move end back one day to show in correct square

        // Check if holiday overlaps with the 6am-6am day boundary
        // Now using < for both comparisons since we've adjusted the boundaries
        if (holidayStart < dayBoundary.end && holidayEnd > dayBoundary.start) {
          return holiday.type;
        }
      }
      return null;
    };

    return (
      <div className="p-6 bg-brand-dark dark:bg-brand-dark/50 border-t border-brand-border dark:border-brand-border">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-brand-gold dark:bg-brand-gold" />
              <span className="text-sm text-muted-foreground dark:text-muted-foreground">Vacation</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-brand-red dark:bg-brand-red" />
              <span className="text-sm text-muted-foreground dark:text-muted-foreground">Sick</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-brand-green dark:bg-brand-green" />
              <span className="text-sm text-muted-foreground dark:text-muted-foreground">Personal</span>
            </div>
          </div>

          <div className="flex flex-col">
            {/* Date numbers row */}
            <div className="flex gap-1 pl-[1px]">
              {days.map((day, i) => (
                <div
                  key={`date-${i}`}
                  className="w-5 text-[10px] text-center text-muted-foreground dark:text-muted-foreground"
                >
                  {day.date.day}
                </div>
              ))}
            </div>
            {/* Blocks row */}
            <div className="flex gap-1">
              {days.map((day, i) => {
                const holidayType = getHolidayTypeForDay(day);

                return (
                  <div
                    key={i}
                    className={`w-5 h-5 rounded-sm ${holidayType ? getHolidayColor(holidayType) : 'bg-brand-dark dark:bg-brand-dark'
                      }`}
                    title={`${day.start.toFormat('MMM d')} (6am) - ${day.date.toFormat('MMM d')} (6am)${holidayType ? ` - ${holidayType}` : ''
                      }`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (usersLoading || holidaysLoading) {
    return (
      <div className="py-8">
        <LoadingState />
        <p className="text-center text-muted-foreground mt-4">
          Loading time off statistics...
        </p>
      </div>
    );
  }

  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Time Off Overview' }
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Breadcrumbs items={breadcrumbs} className="mb-2" />
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
              Time Off Overview
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground">
              View time off statistics for all users this month.
            </p>
          </div>
          <ToggleGroup
            type="single"
            value={departmentFilter}
            onValueChange={(value) => {
              if (value) {
                setDepartmentFilter(value);
                localStorage.setItem(`${STORAGE_KEY_PREFIX}department`, value);
              }
            }}
          >
            <ToggleGroupItem value="all">All</ToggleGroupItem>
            <ToggleGroupItem value="Sales">Sales</ToggleGroupItem>
            <ToggleGroupItem value="Mass DMs">Mass DMs</ToggleGroupItem>
            <ToggleGroupItem value="Posts">Posts</ToggleGroupItem>
            <ToggleGroupItem value="GG Swap">GG Swap</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <ExpandableDataTable
        data={processedData}
        columns={columns}
        initialSorting={[{ id: 'timeOffProgress', desc: true }]}
        pageSize={20}
        searchPlaceholder="Search users..."
        tableId="time-off-overview"
        onRowClick={(row) => expandedRowId == row.user_id ? setExpandedRowId(null) : setExpandedRowId(row.user_id)}
        renderExpanded={renderExpanded}
        getRowId={(row) => row.user_id}
        expandedRowId={expandedRowId || undefined}
      />
    </div>
  );
};

export default TimeOffView; 